<template>
  <div
    class="flex flex-col bg-white"
    :class="flush
      ? 'h-full'
      : 'overflow-hidden rounded-xl shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)]'"
  >
    <div v-if="showHeader" class="px-4 py-6 lg:px-6">
      <h2 class="text-[32px] font-medium leading-10 tracking-[-0.02em] text-code-medical-center-nav lg:text-[36px] lg:leading-[44px]">
        {{ title }}
      </h2>
    </div>

    <template v-if="doctors.length || serviceUuid">
      <div
        class="grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none"
        :class="hideHeaderBar ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'"
        :aria-hidden="hideHeaderBar"
      >
        <div class="min-h-0 overflow-hidden">
          <div class="flex flex-col gap-6 border-y border-neutral-200 bg-neutral-50 px-4 py-6 lg:px-6">
            <div v-if="bookableServiceDetails" class="flex flex-col gap-2">
              <span class="text-sm font-medium leading-4 text-neutral-900">
                {{ $t('booking.service_kind') }}
              </span>
              <div class="flex h-12 items-center gap-3 rounded-lg border border-neutral-400 bg-white px-3">
                <span class="min-w-0 flex-1 truncate text-sm leading-4 text-neutral-900">
                  {{ bookableServiceDetails.name }}
                </span>
                <span
                  v-if="serviceMeta"
                  class="shrink-0 text-sm font-semibold leading-4 text-green-600"
                >
                  {{ serviceMeta }}
                </span>
              </div>
            </div>

            <div
              v-if="isDoctorFocused"
              class="flex flex-wrap items-center justify-between gap-x-3 gap-y-4"
              data-doctor-select
            >
              <button
                type="button"
                class="flex shrink-0 items-center gap-2 text-base font-semibold leading-[18px] text-sky-700 transition-colors hover:text-sky-800"
                @click="unfocusDoctor"
              >
                <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 6l-6 6 6 6" />
                </svg>
                {{ $t('booking.back_to_doctor_selection') }}
              </button>

              <div class="flex min-w-0 flex-1 basis-52 items-center gap-2">
                <span
                  :ref="(el) => registerFocusAvatar(focusedDoctorSlug, el)"
                  class="relative block size-12 shrink-0 overflow-hidden rounded-full"
                >
                  <img
                    v-if="focusedDoctor && focusedDoctor.photo_url"
                    :src="focusedDoctor.photo_url"
                    :alt="focusedDoctor.full_name"
                    class="absolute inset-0 size-full object-cover"
                  >
                  <span
                    v-else
                    class="flex size-full items-center justify-center bg-neutral-100 text-neutral-400"
                  >
                    <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.6"
                        d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z"
                      />
                    </svg>
                  </span>
                </span>
                <div class="flex min-w-0 flex-col gap-1">
                  <span class="text-sm leading-4 text-neutral-500">
                    {{ $t('booking.chosen_specialist') }}
                  </span>
                  <span class="truncate text-base font-normal leading-[18px] text-code-medical-center-nav">
                    {{ focusedDoctor && focusedDoctor.full_name }}
                  </span>
                </div>
              </div>
            </div>

            <div v-else class="flex items-center justify-between gap-2">
              <button
                type="button"
                class="flex size-11 shrink-0 items-center justify-center rounded-full border-2 p-2.5 transition-colors"
                :class="canGoBack
                  ? 'border-code-medical-center-nav text-code-medical-center-nav opacity-90 hover:bg-neutral-100'
                  : 'cursor-not-allowed border-neutral-200 text-neutral-200'"
                :disabled="!canGoBack"
                :aria-label="$t('booking.prev_day')"
                @click="shiftDay(-1)"
              >
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 6l-6 6 6 6" />
                </svg>
              </button>

              <div class="relative flex min-w-0 items-center justify-center gap-3">
                <button
                  ref="calendarButton"
                  type="button"
                  class="flex size-11 shrink-0 items-center justify-center rounded-full border-2 border-code-medical-center-nav p-2.5 text-code-medical-center-nav opacity-90 transition-colors hover:bg-neutral-100"
                  :aria-label="$t('booking.pick_date')"
                  @click="openDatePicker"
                >
                  <Icon name="calendar-days" class="size-5" />
                </button>
                <div class="flex flex-col items-center gap-1 text-center">
                  <span class="text-base font-semibold leading-[18px] text-[#0b4f66]">
                    {{ relativeDayLabel }}
                  </span>
                  <span class="text-sm font-normal leading-4 text-neutral-500">
                    {{ shortDateLabel }}
                  </span>
                </div>
                <input
                  ref="dateInput"
                  type="date"
                  class="pointer-events-none absolute bottom-0 right-0 size-0 opacity-0"
                  :value="selectedIsoDate"
                  :min="todayIso"
                  @change="onDateInput"
                >
              </div>

              <button
                type="button"
                class="flex size-11 shrink-0 items-center justify-center rounded-full border-2 p-2.5 transition-colors"
                :class="canGoForward
                  ? 'border-code-medical-center-nav text-code-medical-center-nav opacity-90 hover:bg-neutral-100'
                  : 'cursor-not-allowed border-neutral-200 text-neutral-200'"
                :disabled="!canGoForward"
                :aria-label="$t('booking.next_day')"
                @click="shiftDay(1)"
              >
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="px-4 pb-6 pt-6 lg:px-6">
        <div
          v-if="isDoctorFocused"
          key="focused"
          data-doctor-select
        >
          <div ref="focusedScroll" class="overflow-x-auto">
            <div
              v-if="focusedDoctorDays.length"
              class="flex min-w-min items-start justify-between gap-2 px-1 pt-1"
            >
              <button
                type="button"
                class="flex size-11 shrink-0 items-center justify-center self-start rounded-full border-2 p-2.5 transition-colors"
                :class="canPrevFocusedDays
                  ? 'border-code-medical-center-nav text-code-medical-center-nav opacity-90 hover:bg-neutral-100'
                  : 'cursor-not-allowed border-neutral-200 text-neutral-200'"
                :disabled="!canPrevFocusedDays"
                :aria-label="$t('booking.prev_dates')"
                @click="shiftFocusedDays(-1)"
              >
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 6l-6 6 6 6" />
                </svg>
              </button>

              <div class="flex flex-1 items-start justify-center gap-6 lg:justify-start">
                <div
                  v-for="day in visibleFocusedDays"
                  :key="day.iso"
                  class="flex w-16 shrink-0 flex-col items-stretch gap-4"
                >
                  <div class="flex flex-col items-center gap-1 text-center leading-tight">
                    <span class="whitespace-nowrap text-base font-semibold leading-[18px] text-code-medical-center-nav">
                      {{ day.relativeLabel }}
                    </span>
                    <span class="text-sm font-normal leading-4 text-neutral-500">
                      {{ day.shortDate }}
                    </span>
                  </div>
                  <div class="flex flex-col items-stretch gap-3">
                    <BookingSlotButton
                      v-for="period in visibleFocusedPeriods(day.periods)"
                      :key="`${day.iso}-${period}`"
                      @click="goToRegistration(focusedDoctorSlug, period, day.iso)"
                    >
                      {{ period }}
                    </BookingSlotButton>
                  </div>
                </div>
              </div>

              <button
                type="button"
                class="flex size-11 shrink-0 items-center justify-center self-start rounded-full border-2 p-2.5 transition-colors"
                :class="canNextFocusedDays
                  ? 'border-code-medical-center-nav text-code-medical-center-nav opacity-90 hover:bg-neutral-100'
                  : 'cursor-not-allowed border-neutral-200 text-neutral-200'"
                :disabled="!canNextFocusedDays"
                :aria-label="$t('booking.next_dates')"
                @click="shiftFocusedDays(1)"
              >
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>

            <div
              v-else
              class="flex h-16 items-center justify-center text-sm leading-5 text-neutral-500"
            >
              {{ $t('booking.no_slots') }}
            </div>
          </div>

          <div
            v-if="hasMoreFocusedPeriods"
            class="mt-4 flex justify-center border-t border-neutral-200 pt-4"
          >
            <button
              type="button"
              data-doctor-select
              class="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-sky-700 hover:text-sky-800"
              @click.stop="showAllPeriods = !showAllPeriods"
            >
              {{ showAllPeriods ? $t('booking.show_less_hours') : $t('booking.show_more_hours') }}
              <svg
                class="size-5 transition-transform"
                :class="{ 'rotate-180': showAllPeriods }"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 7.5 10 12.5 15 7.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div
          v-else
          key="overview"
        >
          <div
            v-if="loading"
            class="py-16 text-center text-sm leading-5 text-neutral-500"
          >
            {{ $t('booking.loading_slots') }}
          </div>

          <div
            v-else-if="loadError"
            class="py-16 text-center text-sm leading-5 text-neutral-500"
          >
            {{ loadError }}
          </div>

          <div
            v-else-if="showInterestCapture && interestSubmitted"
            class="flex flex-col items-center px-1 pt-0 text-center"
          >
            <svg
              class="size-12 shrink-0 text-sky-700"
              viewBox="0 0 48 48"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M10.4 27.15 17.9 34.65 37.6 14.95"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div class="mt-6 w-full text-base font-normal leading-6 text-neutral-900">
              <p>{{ $t('booking.interest_thanks_title') }}</p>
              <p>{{ $t('booking.interest_thanks_body') }}</p>
            </div>
          </div>

          <div
            v-else-if="showInterestCapture"
            class="flex flex-col gap-6"
          >
            <p class="text-base font-normal leading-6 text-neutral-900">
              {{ $t('booking.interest_prompt') }}
            </p>

            <form
              class="relative flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-6"
              @submit.prevent="submitInterest"
            >
              <!-- Honeypot: hidden from humans, bots that autofill it get a fake success. -->
              <div
                aria-hidden="true"
                class="pointer-events-none absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
              >
                <label :for="`${interestFieldId}-company`">Company website</label>
                <input
                  :id="`${interestFieldId}-company`"
                  v-model="interestHoneypot"
                  type="text"
                  name="company_website"
                  tabindex="-1"
                  autocomplete="off"
                >
              </div>

              <div class="flex min-w-0 flex-1 flex-col gap-2">
                <label
                  :for="interestFieldId"
                  class="text-sm font-medium leading-4 text-neutral-900"
                >
                  {{ $t('booking.interest_contact_label') }}
                </label>
                <input
                  :id="interestFieldId"
                  v-model="interestContact"
                  type="text"
                  autocomplete="email tel"
                  class="h-12 w-full rounded-lg border border-neutral-200 bg-white px-3 text-sm leading-4 text-neutral-900 shadow-[0px_1px_1px_rgba(16,24,40,0.04)] outline-none focus:border-code-medical-center focus:ring-1 focus:ring-code-medical-center disabled:opacity-60"
                  :disabled="interestSubmitting"
                >
                <p v-if="interestError" class="text-sm text-red-600">
                  {{ interestError }}
                </p>
              </div>

              <button
                type="submit"
                class="inline-flex h-12 shrink-0 items-center justify-center rounded-lg border border-code-medical-center bg-white px-5 text-base font-semibold leading-[18px] text-code-medical-center shadow-[0px_2px_6px_0px_rgba(16,24,40,0.06)] transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="interestSubmitting"
              >
                {{ $t('booking.interest_submit') }}
              </button>
            </form>
          </div>

          <div
            v-else-if="!hasSlotsOnSelectedDay"
            class="py-16 text-center text-sm leading-5 text-neutral-500"
          >
            {{ $t('booking.no_slots_day') }}
          </div>

          <template v-else>
            <div class="mb-10 flex items-center gap-1 px-1 text-sm font-normal leading-4 text-neutral-500">
              <Icon name="cursor-click" class="size-4 shrink-0" />
              <span>{{ $t('booking.select_doctor_for_more_slots') }}</span>
            </div>
            <div class="overflow-x-auto">
              <div class="flex min-w-min items-start gap-12 px-8">
                <div
                  v-for="col in columns"
                  :key="col.key"
                  data-doctor-select
                  class="flex w-16 shrink-0 cursor-pointer flex-col items-stretch gap-4"
                  @click="onColumnClick(col)"
                >
                  <div class="flex flex-col items-center gap-2 text-center">
                    <span
                      :ref="(el) => registerDayAvatar(col.key, el)"
                      class="relative block size-16 shrink-0 overflow-hidden rounded-full"
                    >
                      <img
                        v-if="col.photoUrl"
                        :src="col.photoUrl"
                        :alt="col.name"
                        class="absolute inset-0 size-full object-cover"
                      >
                      <span
                        v-else
                        class="flex size-full items-center justify-center bg-neutral-100 text-neutral-400"
                      >
                        <svg class="size-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="1.6"
                            d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z"
                          />
                        </svg>
                      </span>
                    </span>

                    <span class="w-full whitespace-pre-wrap text-sm font-normal leading-5 text-neutral-900">
                      {{ col.name }}
                    </span>
                  </div>

                  <div class="flex flex-col items-stretch gap-3">
                    <template v-if="doctorPeriods(col.slug).length">
                      <BookingSlotButton
                        v-for="period in visibleDoctorPeriods(col.slug)"
                        :key="`${col.slug}-${period}`"
                        @click.stop="bookDoctor(col.slug, period)"
                      >
                        {{ period }}
                      </BookingSlotButton>
                    </template>
                    <template v-else>
                      <span
                        v-for="index in overviewSlotRowCount"
                        :key="`${col.slug}-empty-${index}`"
                        class="px-3 py-1.5 text-center text-sm font-medium leading-4 text-neutral-700"
                      >
                        -
                      </span>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="hasMorePeriods"
              class="mt-4 flex justify-center border-t border-neutral-200 pt-4"
            >
              <button
                type="button"
                data-doctor-select
                class="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-sky-700 hover:text-sky-800"
                @click.stop="showAllPeriods = !showAllPeriods"
              >
                {{ showAllPeriods ? $t('booking.show_less_hours') : $t('booking.show_more_hours') }}
                <svg
                  class="size-5 transition-transform"
                  :class="{ 'rotate-180': showAllPeriods }"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 7.5 10 12.5 15 7.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </template>
        </div>
      </div>
    </template>

    <div
      v-else
      class="border-t border-neutral-200 px-4 py-10 text-center text-sm leading-5 text-neutral-500 lg:px-6"
    >
      {{ $t('booking.no_online_specialists') }}
    </div>
  </div>
</template>

<script>
import { router } from '@inertiajs/vue3';
import {
  DEFAULT_MOBILE_DAY_COUNT,
  VISIBLE_DAY_COUNT,
  resolveDayColumns,
} from '@/utils/bookingDayColumns';
import BookingSlotButton from '@/Shared/BookingSlotButton.vue';
import Icon from '@/Shared/Icon.vue';

// How many days ahead we pull in one window fetch. Wide enough to surface the
// nearest free slot on load and to navigate day-by-day without refetching.
const SLOT_DAYS = 60;

// Slots shown per column before the "show more hours" toggle expands the rest.
const INITIAL_PERIOD_COUNT = 5;

function pad(value) {
  return String(value).padStart(2, '0');
}

function toIsoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Parse a 'YYYY-MM-DD' string into a local Date (avoids the UTC parsing that
// `new Date('YYYY-MM-DD')` would apply and which can shift the day).
function parseIsoDate(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function addDaysIso(iso, delta) {
  const date = parseIsoDate(iso);
  date.setDate(date.getDate() + delta);
  return toIsoDate(date);
}

function formatStartDate(date) {
  return `${toIsoDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Sort key for a 'H:MM' / 'HH:MM' period string.
function periodToMinutes(period) {
  const [hours, minutes] = String(period).split(':').map((part) => parseInt(part, 10) || 0);
  return hours * 60 + minutes;
}

export default {
  name: 'ServiceSlotsCalendar',
  components: { BookingSlotButton, Icon },
  props: {
    // The full service payload from the Service Show page. Carries `remote_uuid`
    // (needed to fetch Medfile slots) and `specialists`, each with their own
    // `bookable_services` list used to tell which doctors offer this service
    // online.
    service: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Umów wizytę',
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    flush: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['focus-change'],
  data() {
    return {
      // When set, we leave the day overview and show this doctor's availability
      // across the days they have free slots (the focused single-doctor view).
      focusedDoctorSlug: null,
      // Index of the first day column shown in the focused view (for chevron
      // paging through the doctor's available days).
      focusedDayStart: 0,
      // How many day columns fit in the focused view; recomputed from the
      // container width so wide layouts fill the space with more days.
      // Focused day strip; starts at mobile default and grows with width up to VISIBLE_DAY_COUNT.
      daysPerPage: DEFAULT_MOBILE_DAY_COUNT,
      selectedIsoDate: toIsoDate(new Date()),
      // When false, each column shows at most INITIAL_PERIOD_COUNT slots.
      showAllPeriods: false,
      // Map of specialist slug -> { 'YYYY-MM-DD': period strings } for the
      // currently loaded window.
      slotsByDoctorDate: {},
      // Inclusive date range currently held in slotsByDoctorDate.
      loadedRange: null,
      // When set, the next successful window fetch auto-advances to the nearest
      // day with free slots (used on the initial load).
      autoJumpPending: false,
      loading: false,
      loadError: null,
      // Bumped on each (re)fetch so stale in-flight responses are ignored.
      fetchToken: 0,
      // Empty-calendar lead capture ("Powiadom mnie") — same flow as specialist
      // pages, but without a specialist (service-only admin row).
      interestContact: '',
      interestHoneypot: '',
      interestFormStartedAt: Date.now(),
      interestError: null,
      interestSubmitting: false,
      interestSubmitted: false,
      interestFieldId: `booking-interest-contact-${Math.random().toString(36).slice(2, 9)}`,
    };
  },
  computed: {
    serviceUuid() {
      return this.service?.remote_uuid || null;
    },
    // Doctors of this service that offer it for online booking (their
    // `bookable_services` contains the service's remote_uuid). Only these can
    // return slots, so they become the calendar columns.
    doctors() {
      if (!this.serviceUuid) return [];
      return (this.service?.specialists || []).filter((specialist) => (
        (specialist.bookable_services || []).some((svc) => svc.remote_uuid === this.serviceUuid)
      ));
    },
    // One column per bookable doctor.
    columns() {
      return this.doctors.map((doctor) => ({
        key: doctor.slug,
        slug: doctor.slug,
        name: doctor.full_name,
        photoUrl: doctor.photo_url,
      }));
    },
    // The doctor whose availability is currently expanded, or null in the day
    // overview. Guards against a stale slug after the service changes.
    focusedDoctor() {
      if (!this.focusedDoctorSlug) return null;
      return this.doctors.find((doctor) => doctor.slug === this.focusedDoctorSlug) || null;
    },
    isDoctorFocused() {
      return Boolean(this.focusedDoctor);
    },
    // Days (within the loaded window) on which the focused doctor has free
    // slots, each with a display label and its sorted periods.
    focusedDoctorDays() {
      if (!this.focusedDoctorSlug) return [];
      const byDate = this.slotsByDoctorDate[this.focusedDoctorSlug] || {};
      return Object.keys(byDate)
        .filter((iso) => (byDate[iso] || []).length > 0)
        .sort()
        .map((iso) => ({
          iso,
          relativeLabel: this.relativeLabelFor(iso),
          shortDate: this.shortDateFor(iso),
          periods: byDate[iso],
        }));
    },
    visibleFocusedDays() {
      return this.focusedDoctorDays.slice(this.focusedDayStart, this.focusedDayStart + this.daysPerPage);
    },
    canPrevFocusedDays() {
      return this.focusedDayStart > 0;
    },
    canNextFocusedDays() {
      return this.focusedDayStart + this.daysPerPage < this.focusedDoctorDays.length;
    },
    todayIso() {
      return toIsoDate(new Date());
    },
    // Prev/next only move between days where at least one doctor has free
    // slots — empty calendar days are skipped in the overview.
    canGoBack() {
      return Boolean(this.findAvailableDay(-1));
    },
    canGoForward() {
      return Boolean(this.findAvailableDay(1));
    },
    selectedDate() {
      return parseIsoDate(this.selectedIsoDate);
    },
    weekdayLabel() {
      return this.$t('booking.weekdays_long')[this.selectedDate.getDay()];
    },
    // "Dziś"/"Jutro" for the next two days, otherwise the weekday name — the
    // primary label in the day navigator.
    relativeDayLabel() {
      if (this.selectedIsoDate === this.todayIso) return this.$t('booking.today');
      if (this.selectedIsoDate === addDaysIso(this.todayIso, 1)) return this.$t('booking.tomorrow');
      return this.weekdayLabel;
    },
    // Compact "10 Wrz" secondary label under the relative day.
    shortDateLabel() {
      return this.shortDateFor(this.selectedIsoDate);
    },
    // Combined "duration, price" shown on the right of the service field.
    serviceMeta() {
      return [this.bookableServiceDuration, this.bookableServicePrice].filter(Boolean).join(', ');
    },
    // True when any bookable doctor has at least one free period anywhere in
    // the loaded window — used to decide whether to show the interest form.
    hasAnySlotsInWindow() {
      return this.doctors.some((doctor) => {
        const byDate = this.slotsByDoctorDate[doctor.slug] || {};
        return Object.values(byDate).some((periods) => periods.length > 0);
      });
    },
    hasSlotsOnSelectedDay() {
      return this.doctors.some((doctor) => this.doctorPeriods(doctor.slug).length > 0);
    },
    showInterestCapture() {
      return !this.loading && !this.loadError && !this.hasAnySlotsInWindow;
    },
    // The gray header bar (service field + navigation) stays visible in the
    // overview and the focused single-doctor view; it only collapses when there
    // are no bookable doctors or we're showing the interest form instead.
    hideHeaderBar() {
      return !this.doctors.length || this.showInterestCapture;
    },
    // True when any column holds more slots than we show collapsed, so the
    // "show more / less hours" toggle is offered.
    hasMorePeriods() {
      return this.doctors.some((doctor) => this.doctorPeriods(doctor.slug).length > INITIAL_PERIOD_COUNT);
    },
    // How many placeholder rows empty-doctor columns show — matches the visible
    // slot count in columns that have availability on the selected day.
    overviewSlotRowCount() {
      const counts = this.doctors.map((doctor) => this.doctorPeriods(doctor.slug).length);
      const maxCount = counts.length ? Math.max(...counts) : 0;
      if (this.showAllPeriods) return Math.max(maxCount, INITIAL_PERIOD_COUNT);
      return INITIAL_PERIOD_COUNT;
    },
    hasMoreFocusedPeriods() {
      return this.visibleFocusedDays.some((day) => day.periods.length > INITIAL_PERIOD_COUNT);
    },
    bookableServiceDetails() {
      if (!this.serviceUuid) return null;

      const fromDoctor = this.focusedDoctor?.bookable_services?.find(
        (svc) => svc.remote_uuid === this.serviceUuid,
      );
      if (fromDoctor) return fromDoctor;

      if (!this.service) return null;
      return {
        name: this.service.name,
        price: this.service.price,
        duration: this.service.duration,
      };
    },
    bookableServiceDuration() {
      const { duration } = this.bookableServiceDetails || {};
      if (duration == null || Number.isNaN(Number(duration))) return '';
      return `${Number(duration)} min`;
    },
    bookableServicePrice() {
      const { price } = this.bookableServiceDetails || {};
      if (price == null || Number.isNaN(Number(price))) return '';

      return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
        minimumFractionDigits: Number(price) % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(Number(price)).replace(/\u00a0/g, ' ');
    },
  },
  watch: {
    selectedIsoDate() {
      this.ensureSlotsForSelectedDate();
    },
    serviceUuid: {
      immediate: true,
      handler() {
        this.initialLoad();
      },
    },
    // Keep the doctors list "Wybrany lekarz" button in sync with focus/unfocus.
    focusedDoctorSlug(slug) {
      this.$emit('focus-change', slug);
    },
  },
  created() {
    // Plain (non-reactive) handles to avatar DOM nodes, keyed by column key /
    // doctor slug, used to run the FLIP animation when a doctor is focused.
    this.dayAvatarEls = {};
    this.focusAvatarEls = {};
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    doctorPeriods(slug) {
      return this.slotsByDoctorDate[slug]?.[this.selectedIsoDate] || [];
    },
    visibleDoctorPeriods(slug) {
      const periods = this.doctorPeriods(slug);
      return this.showAllPeriods ? periods : periods.slice(0, INITIAL_PERIOD_COUNT);
    },
    visibleFocusedPeriods(periods) {
      if (this.showAllPeriods) return periods;
      return periods.slice(0, INITIAL_PERIOD_COUNT);
    },
    handleDocumentClick(event) {
      // Clicking anywhere that isn't a doctor-selecting control (a calendar
      // column, a "book" button in the doctors list, or the focused view)
      // returns to the default day overview.
      if (event.target.closest?.('[data-doctor-select]')) return;
      if (this.isDoctorFocused) this.unfocusDoctor();
    },
    onColumnClick(col) {
      this.focusDoctor(col.slug);
    },
    focusDoctor(slug) {
      // Ignore slugs that aren't a bookable doctor of this service.
      if (!this.doctors.some((doctor) => doctor.slug === slug)) return;
      // Snapshot where the column-header avatars sit right now so we can slide
      // the picked one up into the "selected specialist" header slot (FLIP).
      const firstRects = this.captureAvatarRects(this.dayAvatarEls);
      this.focusedDoctorSlug = slug;
      this.focusedDayStart = 0;
      this.showAllPeriods = false;
      this.focusAvatarEls = {};
      this.runAvatarFlip(firstRects, () => this.focusAvatarEls);
      this.$nextTick(() => {
        this.updateFocusedColumns();
      });
    },
    unfocusDoctor() {
      if (!this.isDoctorFocused) return;
      // Mirror focusDoctor: snapshot the header avatar, switch back to overview,
      // then FLIP it down into its column-header position.
      const firstRects = this.captureAvatarRects(this.focusAvatarEls);
      this.focusedDoctorSlug = null;
      this.focusedDayStart = 0;
      this.runAvatarFlip(firstRects, () => this.dayAvatarEls);
    },
    onWindowResize() {
      if (this.isDoctorFocused) this.updateFocusedColumns();
    },
    registerDayAvatar(key, el) {
      if (el) this.dayAvatarEls[key] = el;
      else delete this.dayAvatarEls[key];
    },
    registerFocusAvatar(slug, el) {
      if (el) this.focusAvatarEls[slug] = el;
      else delete this.focusAvatarEls[slug];
    },
    captureAvatarRects(avatarEls) {
      const rects = {};
      Object.keys(avatarEls).forEach((key) => {
        const el = avatarEls[key];
        if (el) rects[key] = el.getBoundingClientRect();
      });
      return rects;
    },
    runAvatarFlip(firstRects, getTargetEls) {
      // FLIP: each target avatar starts at its old position/size, then
      // transitions to its natural spot. Used both when focusing (overview ->
      // focused) and unfocusing (focused -> overview).
      if (!firstRects || !Object.keys(firstRects).length) return;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const targetEls = getTargetEls();
          Object.keys(targetEls).forEach((slug) => {
            const el = targetEls[slug];
            const first = firstRects[slug];
            if (!el || !first) return;

            const last = el.getBoundingClientRect();
            const dx = first.left - last.left;
            const dy = first.top - last.top;
            const scale = last.width ? first.width / last.width : 1;
            if (!dx && !dy && scale === 1) return;

            el.style.transformOrigin = 'top left';
            el.style.transition = 'none';
            el.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
            el.getBoundingClientRect();

            requestAnimationFrame(() => {
              el.style.transition = 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)';
              el.style.transform = '';
            });

            const clear = () => {
              el.style.transition = '';
              el.style.transformOrigin = '';
              el.removeEventListener('transitionend', clear);
            };
            el.addEventListener('transitionend', clear);
          });
        });
      });
    },
    shiftFocusedDays(delta) {
      const maxStart = Math.max(this.focusedDoctorDays.length - this.daysPerPage, 0);
      const next = this.focusedDayStart + delta * this.daysPerPage;
      this.focusedDayStart = Math.min(Math.max(next, 0), maxStart);
    },
    updateFocusedColumns() {
      const el = this.$refs.focusedScroll;
      if (!el) return;
      // lg+: always VISIBLE_DAY_COUNT (5). Mobile: fit as many w-16 columns as width allows.
      this.daysPerPage = resolveDayColumns(el.clientWidth, {
        min: 1,
        max: VISIBLE_DAY_COUNT,
        desktopCount: VISIBLE_DAY_COUNT,
      });
      const maxStart = Math.max(this.focusedDoctorDays.length - this.daysPerPage, 0);
      if (this.focusedDayStart > maxStart) this.focusedDayStart = maxStart;
    },
    // "Dziś"/"Jutro" for the next two days, otherwise the short weekday name.
    relativeLabelFor(iso) {
      if (iso === this.todayIso) return this.$t('booking.today');
      if (iso === addDaysIso(this.todayIso, 1)) return this.$t('booking.tomorrow');
      return this.$t('booking.weekdays_short')[parseIsoDate(iso).getDay()];
    },
    // Compact "10 Wrz" date label (day + abbreviated month).
    shortDateFor(iso) {
      const date = parseIsoDate(iso);
      return `${date.getDate()} ${this.$t('booking.months_short')[date.getMonth()]}`;
    },
    findAvailableDay(delta) {
      if (!this.loadedRange || !delta) return null;

      let cursor = addDaysIso(this.selectedIsoDate, delta);
      const minIso = this.todayIso > this.loadedRange.start ? this.todayIso : this.loadedRange.start;
      const maxIso = this.loadedRange.end;

      while (cursor >= minIso && cursor <= maxIso) {
        if (this.dateHasSlots(cursor)) return cursor;
        cursor = addDaysIso(cursor, delta);
      }

      return null;
    },
    shiftDay(delta) {
      const nextIso = this.findAvailableDay(delta);
      if (!nextIso) return;
      this.selectedIsoDate = nextIso;
    },
    openDatePicker() {
      const input = this.$refs.dateInput;
      if (!input) return;
      if (typeof input.showPicker === 'function') {
        input.showPicker();
      } else {
        input.focus();
        input.click();
      }
    },
    onDateInput(event) {
      const value = event.target.value;
      if (!value || value < this.todayIso) return;

      // Outside the loaded window we accept the pick and refetch; empty days
      // inside the window snap to the nearest day any doctor still has free.
      if (!this.loadedRange || !this.dateInLoadedRange(value) || this.dateHasSlots(value)) {
        this.selectedIsoDate = value;
        return;
      }

      let cursor = value;
      while (cursor <= this.loadedRange.end) {
        if (this.dateHasSlots(cursor)) {
          this.selectedIsoDate = cursor;
          return;
        }
        cursor = addDaysIso(cursor, 1);
      }

      cursor = addDaysIso(value, -1);
      while (cursor >= this.todayIso) {
        if (this.dateHasSlots(cursor)) {
          this.selectedIsoDate = cursor;
          return;
        }
        cursor = addDaysIso(cursor, -1);
      }
    },
    startDateParam(iso) {
      // For today, start from the current time so past slots are skipped;
      // for a future day, start from its midnight.
      if (iso === this.todayIso) {
        return formatStartDate(new Date());
      }
      return `${iso} 00:00`;
    },
    dateInLoadedRange(iso) {
      return Boolean(this.loadedRange) && iso >= this.loadedRange.start && iso <= this.loadedRange.end;
    },
    initialLoad() {
      // Fresh load for the service: clear caches and, once the window arrives,
      // auto-advance to the nearest day that actually has free slots.
      this.focusedDoctorSlug = null;
      this.focusedDayStart = 0;
      this.slotsByDoctorDate = {};
      this.loadedRange = null;
      this.autoJumpPending = true;
      this.resetInterestForm();
      this.fetchWindow(this.selectedIsoDate);
    },
    resetInterestForm() {
      this.interestContact = '';
      this.interestHoneypot = '';
      this.interestFormStartedAt = Date.now();
      this.interestError = null;
      this.interestSubmitting = false;
      this.interestSubmitted = false;
    },
    async submitInterest() {
      const contact = this.interestContact.trim();
      if (!contact) {
        this.interestError = this.$t('booking.interest_contact_required');
        return;
      }
      if (!this.serviceUuid) {
        this.interestError = this.$t('booking.interest_submit_error');
        return;
      }

      this.interestSubmitting = true;
      this.interestError = null;

      try {
        const response = await fetch('/api/booking-interests', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            booking_interest: {
              contact,
              service: this.serviceUuid,
              company_website: this.interestHoneypot,
              form_started_at: this.interestFormStartedAt,
            },
          }),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          const contactErrors = payload.errors?.contact;
          this.interestError = Array.isArray(contactErrors)
            ? contactErrors[0]
            : (payload.error || this.$t('booking.interest_submit_error'));
          return;
        }

        this.interestSubmitted = true;
        this.interestContact = '';
        this.interestHoneypot = '';
      } catch {
        this.interestError = this.$t('booking.interest_submit_error');
      } finally {
        this.interestSubmitting = false;
      }
    },
    ensureSlotsForSelectedDate() {
      // Day navigation only refetches when we step outside the loaded window;
      // within it we already hold every day's periods and just re-filter.
      if (this.dateInLoadedRange(this.selectedIsoDate)) return;
      this.fetchWindow(this.selectedIsoDate);
    },
    async fetchWindow(startIso) {
      if (!this.serviceUuid || !this.doctors.length) {
        this.slotsByDoctorDate = {};
        this.loadedRange = null;
        return;
      }

      const token = (this.fetchToken += 1);
      this.loading = true;
      this.loadError = null;

      const query = new URLSearchParams({
        service: this.serviceUuid,
        days: String(SLOT_DAYS),
        start_date: this.startDateParam(startIso),
      });

      try {
        const results = await Promise.all(
          this.doctors.map((doctor) => this.fetchDoctorWindow(doctor.slug, query)),
        );

        if (token !== this.fetchToken) return;

        const next = {};
        results.forEach(({ slug, byDate }) => {
          next[slug] = byDate;
        });
        this.slotsByDoctorDate = next;
        this.loadedRange = { start: startIso, end: addDaysIso(startIso, SLOT_DAYS - 1) };

        if (this.autoJumpPending) {
          this.autoJumpPending = false;
          this.jumpToNearestAvailable();
        } else if (!this.dateHasSlots(this.selectedIsoDate)) {
          // After refetching a new window (e.g. date picker), never leave the
          // overview parked on a day with no slots across all doctors.
          this.jumpToNearestAvailable();
        }
      } catch {
        if (token === this.fetchToken) {
          this.loadError = this.$t('booking.load_error');
        }
      } finally {
        if (token === this.fetchToken) {
          this.loading = false;
        }
      }
    },
    async fetchDoctorWindow(slug, query) {
      const response = await fetch(
        `/api/specialists/${encodeURIComponent(slug)}/slots?${query.toString()}`,
        { headers: { Accept: 'application/json' } },
      );

      if (!response.ok) {
        // A single doctor's failure shouldn't blank the whole calendar.
        return { slug, byDate: {} };
      }

      const payload = await response.json().catch(() => ({}));
      const byDate = {};
      (payload.slots || []).forEach((slot) => {
        if (!slot?.date) return;
        const periods = Array.isArray(slot.periods) ? [...slot.periods] : [];
        periods.sort((a, b) => periodToMinutes(a) - periodToMinutes(b));
        byDate[slot.date] = periods;
      });

      return { slug, byDate };
    },
    dateHasSlots(iso) {
      return this.doctors.some((doctor) => (this.slotsByDoctorDate[doctor.slug]?.[iso] || []).length > 0);
    },
    jumpToNearestAvailable() {
      // Hop forward to the first day in the loaded window that has at least one
      // doctor's free slot; if the current day already has some, stay put.
      let cursor = this.selectedIsoDate;
      while (cursor <= this.loadedRange.end) {
        if (this.dateHasSlots(cursor)) {
          if (cursor !== this.selectedIsoDate) this.selectedIsoDate = cursor;
          return;
        }
        cursor = addDaysIso(cursor, 1);
      }
    },
    bookDoctor(slug, period) {
      this.goToRegistration(slug, period);
    },
    goToRegistration(slug, period, iso = this.selectedIsoDate) {
      if (!this.serviceUuid || !slug) return;

      const query = new URLSearchParams({
        specialist: slug,
        service: this.serviceUuid,
        date: `${iso} ${period}`,
      });

      router.visit(`/rejestracja?${query.toString()}`);
    },
  },
};
</script>

<style scoped>
button:not(:disabled) {
  cursor: pointer;
}
</style>
